import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CustomModal from "../../component/common/customModal";
import { CreditCardForm } from "../../../components/ui/credit-card-form";
import { buildOppwaPreviewWpwlOptions, setOppwaShopperResultUrl } from "../../../components/ui/oppwaPreviewBridge";
import { buildPaymentCallbackUrl } from "../../../utils/paymentCallbackUrl";
import {
  linkTransactionToOrder,
  markActivePaymentGatewaySubmitted,
  setActivePaymentSession,
} from "../../../utils/abandonedOrderPayments";
import {
  CancelGatewayOutcome,
  handlePaymentClose,
} from "../../../utils/paymentCloseCleanup";
import { useTranslation } from "react-i18next";
import { NJIM } from "../../../theme/njimTheme";

declare global {
  interface Window {
    wpwlOptions?: ReturnType<typeof buildOppwaPreviewWpwlOptions> & Record<string, unknown>;
    wpwl?: { unload?: () => void };
  }
}

interface Props {
  open: boolean;
  onClose: (outcome?: CancelGatewayOutcome) => void;
  checkoutId: string;
  orderId?: string;
  action?: string;
  amount?: number;
  currency?: string;
  transactionStorageKey?: "lastCardPaymentTransactionId" | "lastCardTopUpTransactionId";
}

const CardPaymentForm: React.FC<Props> = ({
  open,
  onClose,
  checkoutId,
  orderId,
  action,
  amount,
  currency = "IQD",
  transactionStorageKey = "lastCardPaymentTransactionId",
}) => {
  const { t } = useTranslation();
  const [loadKey, setLoadKey] = useState(0);
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);

  const formAction =
    action ||
    buildPaymentCallbackUrl({ returnTo: "saleOrder", type: "CardPayment", checkoutId });

  useEffect(() => {
    if (checkoutId) {
      localStorage.setItem(transactionStorageKey, checkoutId);
    }
    setOppwaShopperResultUrl(formAction);
  }, [checkoutId, formAction, transactionStorageKey]);

  useEffect(() => {
    if (!open || !checkoutId || !orderId) return;
    linkTransactionToOrder(checkoutId, orderId);
    setActivePaymentSession({
      orderId,
      type: "CardPayment",
      transactionId: checkoutId,
    });
  }, [open, checkoutId, orderId]);

  const loadWidgetScript = useCallback(() => {
    if (!checkoutId || !open) return;

    setOppwaShopperResultUrl(formAction);
    const baseOptions = buildOppwaPreviewWpwlOptions();
    window.wpwlOptions = {
      ...baseOptions,
      // Fires right before HyperPay submits the card. Flag the payment session so the
      // tab-close guard does not cancel this payment when the browser leaves for 3-D Secure.
      onBeforeSubmitCard: () => {
        markActivePaymentGatewaySubmitted();
        return true;
      },
    };

    const script = document.createElement("script");
    script.src = `https://eu-prod.oppwa.com/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
    script.async = true;
    script.dataset.oppwaWidget = "true";

    document.body.appendChild(script);
  }, [checkoutId, open, formAction]);

  useEffect(() => {
    if (!checkoutId || !open) return;

    loadWidgetScript();

    return () => {
      try {
        window.wpwl?.unload?.();
      } catch {
        /* ignore unload errors */
      }
      document.querySelectorAll("script[data-oppwa-widget]").forEach((el) => el.remove());
      delete window.wpwlOptions;
    };
  }, [checkoutId, open, loadKey, loadWidgetScript]);

  const handleRetry = useCallback(() => {
    try {
      window.wpwl?.unload?.();
    } catch {
      /* ignore */
    }
    document.querySelectorAll("script[data-oppwa-widget]").forEach((el) => el.remove());
    setLoadKey((k) => k + 1);
  }, []);

  const handleClose = useCallback(async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    let outcome: CancelGatewayOutcome | undefined;
    try {
      if (orderId && checkoutId) {
        outcome = await handlePaymentClose({
          orderId,
          type: "CardPayment",
          transactionId: checkoutId,
        });
      }
    } finally {
      closingRef.current = false;
      setClosing(false);
      onClose(outcome);
    }
  }, [checkoutId, onClose, orderId]);

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      hideHeader
      width={{ xs: "95%", sm: "92%", md: "960px", lg: "980px" }}
      paperSx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        maxHeight: { xs: "94vh", md: "90vh" },
        overflow: "auto",
      }}
    >
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(93,183,167,0.14)",
              border: "1px solid rgba(93,183,167,0.28)",
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 18, color: NJIM.mint }} aria-hidden />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{ fontSize: { xs: 17, sm: 18 }, fontWeight: 700, color: NJIM.ink, lineHeight: 1.25 }}
            >
              {t("invoicesPayments.cardPayment", { defaultValue: "Card Payment" })}
            </Typography>
            <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, mt: 0.35, lineHeight: 1.4 }}>
              {t("invoicesPayments.cardPaymentSubtitle", {
                defaultValue: "Complete your payment securely.",
              })}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={closing}
          aria-label={t("common.close", { defaultValue: "Close" })}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            bgcolor: NJIM.muted,
            border: `1px solid ${NJIM.border}`,
            flexShrink: 0,
            "&:hover": { bgcolor: NJIM.muted },
            "&:focus-visible": { outline: "2px solid rgba(93,183,167,0.5)", outlineOffset: 2 },
          }}
        >
          {closing ? (
            <CircularProgress size={18} sx={{ color: NJIM.textSecondary }} />
          ) : (
            <CloseIcon sx={{ fontSize: 20, color: NJIM.textSecondary }} />
          )}
        </IconButton>
      </Box>

      <CreditCardForm
        key={`${checkoutId}-${loadKey}`}
        className="credit-card-form--modal"
        amount={amount}
        currency={currency}
        showSubmit={false}
        onWidgetError={handleRetry}
        widgetSlot={
          <form
            action={formAction}
            className="paymentWidgets credit-card-form__oppwa"
            data-brands="VISA MASTER AMEX"
          />
        }
      />
    </CustomModal>
  );
};

export default CardPaymentForm;
